import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Learning: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');

  const categories = [
    { id: 'all', name: t('all_resources', 'All Resources'), icon: 'ri-book-line' },
    { id: 'basics', name: t('basics', 'Basics'), icon: 'ri-lightbulb-line' },
    { id: 'techniques', name: t('techniques', 'Techniques'), icon: 'ri-hand-heart-line' },
    { id: 'strategy', name: t('strategy', 'Strategy'), icon: 'ri-brain-line' },
    { id: 'equipment', name: t('equipment', 'Equipment'), icon: 'ri-tools-line' },
    { id: 'rules', name: t('rules', 'Rules'), icon: 'ri-file-list-3-line' }
  ];

  const resources = [
    // English Resources
    {
      id: 1,
      title: 'Pickleball Basics: Getting Started',
      title_zh: '匹克球基礎：入門指南',
      category: 'basics',
      type: 'video',
      duration: '12:30',
      difficulty: 'Beginner',
      description: 'Learn the fundamental rules, court layout, and basic equipment needed to start playing pickleball.',
      description_zh: '學習匹克球的基本規則、球場佈局和開始打球所需的基本設備。',
      thumbnail: 'https://public.readdy.ai/ai/img_res/0fc16200b76681fa71052ce70cc16e33.jpg',
      author: 'Coach Sarah Chen',
      author_zh: '陳教練',
      views: 15420,
      rating: 4.8,
      language: 'en'
    },
    {
      id: 2,
      title: 'Mastering the Third Shot Drop',
      title_zh: '掌握第三拍放小球',
      category: 'techniques',
      type: 'video',
      duration: '18:45',
      difficulty: 'Intermediate',
      description: 'Advanced technique tutorial on executing the perfect third shot drop to gain control of the net.',
      description_zh: '進階技術教學，學習如何執行完美的第三拍放小球以控制網前。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Professional%20pickleball%20player%20demonstrating%20third%20shot%20drop%20technique%20on%20court%2C%20action%20shot%20showing%20proper%20form%20and%20paddle%20position%2C%20instructional%20sports%20photography%20style&width=400&height=225&seq=learn2&orientation=landscape',
      author: 'Pro Player Mike Wong',
      author_zh: '職業選手黃志明',
      views: 8930,
      rating: 4.9,
      language: 'en'
    },
    {
      id: 3,
      title: 'Doubles Strategy Guide',
      title_zh: '雙打策略指南',
      category: 'strategy',
      type: 'article',
      readTime: '8 min read',
      readTime_zh: '8分鐘閱讀',
      difficulty: 'Intermediate',
      description: 'Comprehensive guide to positioning, communication, and tactical play in doubles pickleball.',
      description_zh: '雙打匹克球中定位、溝通和戰術打法的全面指南。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Overhead%20view%20of%20pickleball%20doubles%20court%20showing%20player%20positions%20and%20movement%20patterns%2C%20tactical%20diagram%20with%20arrows%20and%20strategic%20positioning%20illustrations&width=400&height=225&seq=learn3&orientation=landscape',
      author: 'Coach Lisa Park',
      author_zh: '朴教練',
      views: 12650,
      rating: 4.7,
      language: 'en'
    },
    {
      id: 4,
      title: 'Choosing Your First Paddle',
      title_zh: '選擇您的第一支球拍',
      category: 'equipment',
      type: 'guide',
      readTime: '6 min read',
      readTime_zh: '6分鐘閱讀',
      difficulty: 'Beginner',
      description: 'Everything you need to know about paddle materials, weight, grip size, and finding the right paddle for your playing style.',
      description_zh: '關於球拍材料、重量、握把尺寸以及找到適合您打球風格的球拍的所有知識。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Collection%20of%20different%20pickleball%20paddles%20arranged%20on%20clean%20white%20background%2C%20showing%20various%20paddle%20designs%20and%20materials%2C%20product%20photography%20style%20with%20good%20lighting&width=400&height=225&seq=learn4&orientation=landscape',
      author: 'Equipment Expert David Kim',
      author_zh: '設備專家金大衛',
      views: 9840,
      rating: 4.6,
      language: 'en'
    },
    // Chinese Resources
    {
      id: 5,
      title: '匹克球基礎教學',
      title_zh: '匹克球基礎教學',
      category: 'basics',
      type: 'video',
      duration: '15:20',
      duration_zh: '15分20秒',
      difficulty: 'Beginner',
      description: '完整的匹克球入門指南，包含規則說明、基本技巧和球場禮儀',
      description_zh: '完整的匹克球入門指南，包含規則說明、基本技巧和球場禮儀',
      thumbnail: 'https://readdy.ai/api/search-image?query=Chinese%20pickleball%20instructor%20teaching%20basics%20on%20court%2C%20clear%20demonstration%20of%20rules%20and%20techniques%2C%20educational%20sports%20content%20with%20Asian%20coach&width=400&height=225&seq=learn5&orientation=landscape',
      author: '李教練',
      author_zh: '李教練',
      views: 22100,
      rating: 4.9,
      language: 'zh'
    },
    {
      id: 6,
      title: '雙打戰術與配合',
      title_zh: '雙打戰術與配合',
      category: 'strategy',
      type: 'video',
      duration: '22:15',
      duration_zh: '22分15秒',
      difficulty: 'Intermediate',
      description: '學習雙打比賽中的團隊配合、溝通技巧和戰術運用，提升比賽表現',
      description_zh: '學習雙打比賽中的團隊配合、溝通技巧和戰術運用，提升比賽表現',
      thumbnail: 'https://readdy.ai/api/search-image?query=Pickleball%20doubles%20strategy%20demonstration%20with%20Chinese%20players%2C%20teamwork%20and%20positioning%20on%20court%2C%20tactical%20sports%20instruction&width=400&height=225&seq=learn6&orientation=landscape',
      author: '王教練',
      author_zh: '王教練',
      views: 18200,
      rating: 4.8,
      language: 'zh'
    },
    {
      id: 7,
      title: '進階技巧訓練',
      title_zh: '進階技巧訓練',
      category: 'techniques',
      type: 'video',
      duration: '16:30',
      duration_zh: '16分30秒',
      difficulty: 'Advanced',
      description: '提升球技的進階訓練方法，包含各種擊球技巧、旋轉球和戰術應用',
      description_zh: '提升球技的進階訓練方法，包含各種擊球技巧、旋轉球和戰術應用',
      thumbnail: 'https://readdy.ai/api/search-image?query=Advanced%20pickleball%20training%20techniques%20with%20Chinese%20coach%2C%20professional%20skill%20development%20and%20advanced%20shots%20demonstration&width=400&height=225&seq=learn7&orientation=landscape',
      author: '陳教練',
      author_zh: '陳教練',
      views: 14650,
      rating: 4.9,
      language: 'zh'
    },
    {
      id: 8,
      title: '比賽規則詳解',
      title_zh: '比賽規則詳解',
      category: 'rules',
      type: 'article',
      readTime: '10分鐘閱讀',
      readTime_zh: '10分鐘閱讀',
      difficulty: 'All Levels',
      description: '詳細解說匹克球的官方比賽規則、計分方式、發球規則和裁判標準',
      description_zh: '詳細解說匹克球的官方比賽規則、計分方式、發球規則和裁判標準',
      thumbnail: 'https://readdy.ai/api/search-image?query=Pickleball%20official%20rules%20explanation%20in%20Chinese%2C%20referee%20guidelines%20and%20scoring%20system%2C%20educational%20sports%20content&width=400&height=225&seq=learn8&orientation=landscape',
      author: '香港匹克球協會',
      author_zh: '香港匹克球協會',
      views: 16420,
      rating: 4.7,
      language: 'zh'
    },
    {
      id: 9,
      title: '設備選擇指南',
      title_zh: '設備選擇指南',
      category: 'equipment',
      type: 'guide',
      readTime: '8分鐘閱讀',
      readTime_zh: '8分鐘閱讀',
      difficulty: 'Beginner',
      description: '如何選擇適合的球拍、球鞋和其他匹克球裝備，新手購買建議',
      description_zh: '如何選擇適合的球拍、球鞋和其他匹克球裝備，新手購買建議',
      thumbnail: 'https://readdy.ai/api/search-image?query=Pickleball%20equipment%20guide%20in%20Chinese%2C%20paddle%20selection%20and%20gear%20recommendations%2C%20shopping%20guide%20for%20beginners&width=400&height=225&seq=learn9&orientation=landscape',
      author: '裝備專家張先生',
      author_zh: '裝備專家張先生',
      views: 11234,
      rating: 4.6,
      language: 'zh'
    },
    {
      id: 10,
      title: '發球技巧大全',
      title_zh: '發球技巧大全',
      category: 'techniques',
      type: 'video',
      duration: '20:45',
      duration_zh: '20分45秒',
      difficulty: 'Intermediate',
      description: '掌握各種發球技巧，包含力量發球、放置發球和旋轉發球的訓練方法',
      description_zh: '掌握各種發球技巧，包含力量發球、放置發球和旋轉發球的訓練方法',
      thumbnail: 'https://readdy.ai/api/search-image?query=Pickleball%20serving%20techniques%20demonstration%20by%20Chinese%20instructor%2C%20different%20serve%20styles%20and%20training%20methods&width=400&height=225&seq=learn10&orientation=landscape',
      author: '劉教練',
      author_zh: '劉教練',
      views: 13800,
      rating: 4.8,
      language: 'zh'
    }
  ];

  const filteredResources = resources.filter(resource => {
    const matchesCategory = activeCategory === 'all' || resource.category === activeCategory;
    const matchesLanguage = languageFilter === 'all' || resource.language === languageFilter;
    const currentTitle = resource.language === 'zh' ? resource.title_zh : resource.title;
    const currentDescription = resource.language === 'zh' ? resource.description_zh : resource.description;
    const matchesSearch = currentTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         currentDescription.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesLanguage && matchesSearch;
  });

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return 'ri-play-circle-line';
      case 'article': return 'ri-article-line';
      case 'guide': return 'ri-guide-line';
      case 'document': return 'ri-file-text-line';
      default: return 'ri-book-line';
    }
  };

  const getCurrentLanguage = () => {
    return i18n.language === 'zh-TW' ? 'zh' : 'en';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">{t('learning_center', 'Learning Center')}</h1>
            <p className="text-xl text-teal-100">{t('master_skills', 'Master your pickleball skills with expert tutorials and guides')}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="flex flex-col gap-6">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('search_tutorials', 'Search tutorials, guides, and resources...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              </div>
            </div>

            {/* Language Filter */}
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="text-sm font-medium text-gray-700 flex items-center mr-4">Language:</span>
              <button
                onClick={() => setLanguageFilter('all')}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  languageFilter === 'all'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-global-line mr-2"></i>
                All Languages
              </button>
              <button
                onClick={() => setLanguageFilter('en')}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  languageFilter === 'en'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-flag-line mr-2"></i>
                English
              </button>
              <button
                onClick={() => setLanguageFilter('zh')}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  languageFilter === 'zh'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-flag-2-line mr-2"></i>
                中文
              </button>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    activeCategory === category.id
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${category.icon} mr-2`}></i>
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Featured Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('featured_content', 'Featured Content')}</h2>
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="md:flex">
              <div className="md:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20pickleball%20instructor%20teaching%20group%20of%20students%20on%20outdoor%20court%2C%20engaging%20educational%20scene%20with%20clear%20instruction%20and%20learning%20environment%2C%20bright%20natural%20lighting&width=600&height=400&seq=featured&orientation=landscape"
                  alt="Featured Content"
                  className="w-full h-64 md:h-full object-cover object-top"
                />
              </div>
              <div className="md:w-1/2 p-8">
                <div className="flex items-center mb-4">
                  <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium mr-3">
                    {t('new_course', 'New Course')}
                  </span>
                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                    {t('intermediate', 'Intermediate')}
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {getCurrentLanguage() === 'zh' 
                    ? '完整匹克球精通課程' 
                    : t('complete_mastery', 'Complete Pickleball Mastery Course')
                  }
                </h3>
                <p className="text-gray-600 mb-6">
                  {getCurrentLanguage() === 'zh'
                    ? '涵蓋從基本原理到高級錦標賽策略的全面10部分影片系列。非常適合希望將比賽提升到新水平的球員。'
                    : t('comprehensive_series', 'A comprehensive 10-part video series covering everything from basic fundamentals to advanced tournament strategies. Perfect for players looking to take their game to the next level.')
                  }
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-gray-600">
                    <i className="ri-play-circle-line mr-2"></i>
                    <span>
                      10 {getCurrentLanguage() === 'zh' ? '部影片 • 3.5 小時' : t('videos_hours', 'videos • 3.5 hours')}
                    </span>
                  </div>
                  <button className="bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                    {getCurrentLanguage() === 'zh' ? '開始學習' : t('start_learning', 'Start Learning')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Resources Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {activeCategory === 'all' ? t('all_resources', 'All Resources') : categories.find(c => c.id === activeCategory)?.name}
              {languageFilter !== 'all' && (
                <span className="text-lg font-normal text-gray-600 ml-2">
                  ({languageFilter === 'en' ? 'English' : '中文'})
                </span>
              )}
            </h2>
            <p className="text-gray-600">
              {filteredResources.length} {t('resources_found', 'resources found')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map(resource => (
              <div key={resource.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                <div className="relative">
                  <img
                    src={resource.thumbnail}
                    alt={resource.language === 'zh' ? resource.title_zh : resource.title}
                    className="w-full h-48 object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 flex space-x-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(resource.difficulty)}`}>
                      {getCurrentLanguage() === 'zh' && resource.difficulty === 'All Levels' ? '所有等級' :
                       getCurrentLanguage() === 'zh' && resource.difficulty === 'Beginner' ? '初學者' :
                       getCurrentLanguage() === 'zh' && resource.difficulty === 'Intermediate' ? '中級' :
                       getCurrentLanguage() === 'zh' && resource.difficulty === 'Advanced' ? '進階' :
                       t(resource.difficulty.toLowerCase(), resource.difficulty)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      resource.language === 'en' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {resource.language === 'en' ? 'EN' : '中文'}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-sm flex items-center">
                    <i className={`${getTypeIcon(resource.type)} mr-1`}></i>
                    {resource.language === 'zh' ? 
                      (resource.duration_zh || resource.readTime_zh || resource.duration || resource.readTime) :
                      (resource.duration || resource.readTime)
                    }
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {resource.language === 'zh' ? resource.title_zh : resource.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {resource.language === 'zh' ? resource.description_zh : resource.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <span>
                      {getCurrentLanguage() === 'zh' ? '作者：' : 'by '}
                      {resource.language === 'zh' ? resource.author_zh : resource.author}
                    </span>
                    <div className="flex items-center">
                      <i className="ri-eye-line mr-1"></i>
                      <span>{resource.views.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex items-center mr-2">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-${i < Math.floor(resource.rating) ? 'fill' : 'line'} text-yellow-400 text-sm`}
                          ></i>
                        ))}
                      </div>
                      <span className="text-sm text-gray-600">{resource.rating}</span>
                    </div>
                    <button className="text-teal-600 hover:text-teal-700 font-medium text-sm whitespace-nowrap cursor-pointer">
                      {resource.type === 'video' ? 
                        (getCurrentLanguage() === 'zh' ? '立即觀看' : t('watch_now', 'Watch Now')) : 
                        (getCurrentLanguage() === 'zh' ? '閱讀更多' : t('read_more', 'Read More'))
                      }
                      <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* No Results */}
        {filteredResources.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-search-line text-6xl text-gray-300 mb-4"></i>
            <h3 className="text-xl font-medium text-gray-900 mb-2">{t('no_resources_found', 'No resources found')}</h3>
            <p className="text-gray-600">
              {languageFilter === 'all' 
                ? t('try_adjusting_search', 'Try adjusting your search or category filter')
                : `No ${languageFilter === 'en' ? 'English' : 'Chinese'} resources found for your search criteria`
              }
            </p>
          </div>
        )}

        {/* Learning Path Section */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('recommended_learning', 'Recommended Learning Path')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-seedling-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('start_with_basics', '1. Start with Basics')}</h3>
              <p className="text-gray-600 text-sm">{t('learn_rules_layout', 'Learn rules, court layout, and basic equipment')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-trophy-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('master_techniques', '2. Master Techniques')}</h3>
              <p className="text-gray-600 text-sm">{t('practice_serves', 'Practice serves, volleys, and shot placement')}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-brain-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">{t('develop_strategy', '3. Develop Strategy')}</h3>
              <p className="text-gray-600 text-sm">{t('learn_positioning', 'Learn positioning, tactics, and game strategy')}</p>
            </div>
          </div>
        </div>

        {/* Community Section */}
        <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('join_learning_community', 'Join Our Learning Community')}</h2>
          <p className="text-gray-600 mb-6">
            {t('connect_with_players', 'Connect with other players, ask questions, and share your progress in our community forum.')}
          </p>
          <Link
            to="/signup"
            className="inline-block bg-teal-600 text-white px-6 py-3 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            {getCurrentLanguage() === 'zh' ? '加入社群' : t('join_community', 'Join Community')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Learning;
